// src/components/pages/ApprovePage.vue
<script setup>
import { watch, ref, computed } from 'vue'
import { useDataStore } from '@/stores/data'
import { useAppStore } from '@/stores/app'
import { useMapStore } from '@/stores/map'
import BasePage from './BasePage.vue'
import SelectBox from '@/components/shared/SelectBox.vue'
import { getIconForLocation, clearSurveyAnswers, calculateLocationDistance, autoCompleteSurveyForJourney } from '@/utils/journeyUtils'
import JourneySurveyQuestion from '@/components/shared/JourneySurveyQuestion.vue'
import geoService from '@/services/geoService'
import cloneDeep from 'lodash/cloneDeep'

const dataStore = useDataStore()
const appStore = useAppStore()
const mapStore = useMapStore()
const selectedDestination = ref(null)
const showJourneyDaySurvey = ref(false)
const clonedCurrentJourney = ref(null)
const surveyPlacesCount = ref(0)

// Set a reference in datastore for debugging purposes
dataStore.currentJourneyClone = clonedCurrentJourney

const searchDestination = async (query) => {
	// Include location bias for the search
	var destinationLocation = clonedCurrentJourney.value?.destination ?? null
	const result = await geoService.search(query, destinationLocation)
	console.warn('ApprovePage: searchDestination', query, result)
	return result
}

const setDestinationForJourney = (journey, placeDetails) => {
	setJourneyDestinationDataFromPlace(journey.destination, placeDetails)
	var lastTrip = journey.trips[journey.trips.length - 1]
	lastTrip.locations[lastTrip.locations.length - 1].latitude = placeDetails.latitude
	lastTrip.locations[lastTrip.locations.length - 1].longitude = placeDetails.longitude
	setJourneyDestinationDataFromPlace(lastTrip.destination, placeDetails)
}

const handleDestinationListOpen = (openState) => {
	console.warn('ApprovePage: handleDestinationListOpen', openState)
	const isFullscreen = appStore.bottomSheet.currentHeight == appStore.bottomSheet.BottomSheetStates.FULLSCREEN
	if (openState && !isFullscreen) appStore.bottomSheet.setHeightLarge('500', 'ApprovePage: handleDestinationListOpen', false)
}

const handleDestinationChange = async (enableFullscreen = false) => {
	console.warn('ApprovePage: handleDestinationChange', selectedDestination.value, clonedCurrentJourney.value)
	if (!selectedDestination.value) return

	// Clear survey answers when the destination changes
	clearSurveyAnswers(clonedCurrentJourney.value.survey)

	// If selected place is actually a POI then use google to find the place and add google_place_id
	if (!selectedDestination.value.google_place_id && selectedDestination.value.place_id.startsWith('poi_')) {
		// Find place details via google text search
		const placeWithGooglePlaceId = await geoService.getGooglePlaceId(selectedDestination.value)
		if (placeWithGooglePlaceId && placeWithGooglePlaceId.google_place_id) {
			setJourneyDestinationDataFromPlace(selectedDestination.value, placeWithGooglePlaceId)
		}
	}

	// Find place details if not already fetched
	if (!selectedDestination.value.place_id) {
		// Get place details which will include latitude and longitude
		const placeWithDetails = await geoService.getPlaceDetails(selectedDestination.value)
		if (placeWithDetails) setJourneyDestinationDataFromPlace(selectedDestination.value, placeWithDetails)
	}

	setDestinationForJourney(clonedCurrentJourney.value, selectedDestination.value)

	// Plot trips without fitbound for map
	mapStore.plotTripsInner(clonedCurrentJourney.value.trips, false)

	// Only apply auto-complete if the survey is not already answered
	if (clonedCurrentJourney.value?.survey.length && clonedCurrentJourney.value?.survey[0]?.answer == '') {
		// Try to autocomplete the survey based on the destination
		dataStore.autoCompleteSurveyForJourneyDestination(clonedCurrentJourney.value)
	}

	// Evaluate the current journey survey responses
	dataStore.evaluateCurrentJourneySurveyResponses(clonedCurrentJourney.value)

	// Resize bottom sheet and fly to location
	appStore.bottomSheet.setHeightLarge('500', 'ApprovePage: handleDestinationChange', false, function () {
		// Fly to the selected destination after resize
		mapStore.flyToLocation(clonedCurrentJourney.value.destination, 16, 1000)
	})
}

// Update some of the destination properties from a place object
const setJourneyDestinationDataFromPlace = (destination, place) => {
	if (!destination || !place) return
	destination.place_id = place.place_id ?? place.id
	destination.type = place.type
	destination.title = place.title
	destination.subTitle = place.subTitle
	destination.categories = place.categories
	destination.latitude = place.latitude
	destination.longitude = place.longitude
	destination.placeTypes = place.placeTypes
	destination.formattedAddress = place.formattedAddress
	destination.surveyResponses = place.surveyResponses
}

const defaultPlaces = ref([])

const setBottomSheetFullscreen = () => {
	if ((Array.isArray(clonedCurrentJourney.value.survey) && clonedCurrentJourney.value.survey[0].answer) || (!Array.isArray(clonedCurrentJourney.value.survey) && clonedCurrentJourney.value.survey.answer)) {
		appStore.bottomSheet.setHeightFullscreen(500, 'ApprovePage: setBottomSheetFullscreen', true)
	}
}

const autoCompleteJourneySurvey = (eventData) => {
	const baseId = eventData.survey?.id?.replace(/_R\d+.*$/, '')
	console.warn('ApprovePage: autoCompleteJourneySurvey', eventData, baseId)
	if (eventData.selectedOption && (baseId == 'FLOKK_REISE' || baseId == 'FLOKK_ANTALL' || baseId == 'FLOKK_BARN')) {
		// Make sure dataStore.currentJourneyDay.autofill exists
		if (!dataStore.currentJourneyDay.autofill) dataStore.currentJourneyDay.autofill = {}
		dataStore.currentJourneyDay.autofill[baseId] = cloneDeep(eventData.selectedOption)
	}
}

const surveyChanged = (eventData) => {
	console.warn('ApprovePage: surveyChanged', eventData)

	// Try to auto-complete the survey
	if (eventData) autoCompleteJourneySurvey(eventData)

	// Make page fullscreen before showing
	if (showJourneyDaySurvey.value) {
		dataStore.evaluateCurrentJourneyDaySurveyResponses()
	} else {
		dataStore.evaluateCurrentJourneySurveyResponses(clonedCurrentJourney.value)
		console.warn('ApprovePage: surveyChanged', 'evaluateCurrentJourneySurveyResponses', Array.isArray(clonedCurrentJourney.value.survey), clonedCurrentJourney.value)
	}

	if (eventData?.survey) setBottomSheetFullscreen()
}

const surveyOptionsOpened = (isOpen) => {
	console.warn('ApprovePage: surveyOptionsOpened', isOpen)
	setTimeout(() => {
		document.querySelector('.journey.question:has(.selected.open +.items)')?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
	}, 200)
}

const approveJourneyClick = () => {
	if (!validJourney.value) {
		console.warn('ApprovePage: approveTrip click', 'Invalid journey')
		return
	} else {
		console.warn('ApprovePage: approveTrip click', showJourneyDaySurvey.value)
	}

	// Update current journey with the cloned journey
	dataStore.setCurrentJourney(clonedCurrentJourney.value)

	if (!showJourneyDaySurvey.value) {
		// Check if we need to show journey day survey
		if (dataStore.currentJourneyDay.survey && Object.keys(dataStore.currentJourneyDay.survey).length && dataStore.currentJourneyIndex == dataStore.currentJourneyDay.journeys.length - 1) {
			showJourneyDaySurvey.value = true
		} else {
			showJourneyDaySurvey.value = false
			dataStore.approveJourney()
		}
	} else {
		dataStore.approveJourney()
	}

	// Clear the edit trip state in map
	clearMapEditTrip()
}

const validJourney = computed(() => {
	try {
		const isValid = !!clonedCurrentJourney.value?.destination?.place_id && !!clonedCurrentJourney.value?.surveyJourneyCompleted
		console.warn('ApprovePage: validJourney', isValid, clonedCurrentJourney.value?.surveyJourneyCompleted)
		return isValid
	} catch (error) {
		console.warn('Error in validJourney computed:', error)
		return false
	}
})

const validDay = computed(() => {
	try {
		let isValid = dataStore.currentJourneyDay?.surveyDayCompleted
		console.warn('ApprovePage: validDay', isValid, dataStore.currentJourneyDay?.surveyDayCompleted)
		return isValid
	} catch (error) {
		console.warn('Error in validDay computed:', error)
		return false
	}
})

const mapLocationHandler = (lngLat) => {
	console.warn('ApprovePage: map-location', lngLat)
	const location = { latitude: lngLat.lat, longitude: lngLat.lng }
	geoService.getPlaceOrAddressForLocation(location).then((place) => {
		selectedDestination.value = place
		handleDestinationChange()
	})
}

const initMapEditTrip = () => {
	dataStore.currentLocationType = 'destination'
	// Handle long press on the map to set destination
	mapStore.setCoordinateChangeListener(mapLocationHandler)
	mapStore.currentEditTrip = clonedCurrentJourney.value.trips[clonedCurrentJourney.value.trips.length - 1]
	mapStore.updateMapForEdit()
}

const init = async () => {
	showJourneyDaySurvey.value = false
	selectedDestination.value = null

	// Clone the current journey to avoid changing the original object before saving
	clonedCurrentJourney.value = cloneDeep(dataStore.currentJourney)

	initMapEditTrip()

	console.warn('ApprovePage: Init', cloneDeep(selectedDestination.value))

	if (dataStore?.surveyDefinitions?.variables?.places) {
		// Only use places that are closes then 500 meters from clonedCurrentJourney.value.destination
		defaultPlaces.value = cloneDeep(dataStore?.surveyDefinitions?.variables?.places).filter((place) => {
			if (clonedCurrentJourney.value.destination && place.latitude && place.longitude) {
				const distance = calculateLocationDistance(clonedCurrentJourney.value.destination, place)
				return distance < 500
			}
			return []
		})
		console.warn('ApprovePage: Init', 'defaultPlaces', defaultPlaces.value.length, defaultPlaces.value)
		// Set acualt places count before adding nearby places
		surveyPlacesCount.value = defaultPlaces.value.length - 1
	}

	// If the destination is not set with place_id, lets suggest nearby places
	if (!clonedCurrentJourney.value.destination.place_id) {
		// Get addresses for the destination
		const locationAddress = await geoService.getAddressForLocation(clonedCurrentJourney.value.destination)
		locationAddress.extraTitle = 'Nærmeste adresse til registrert lokasjon'
		locationAddress.subTitle = locationAddress.formattedAddress
		console.warn('ApprovePage: Init', 'locationAddress', locationAddress)
		defaultPlaces.value.push(locationAddress)

		// Find nearby pois based on the destination
		// This is disabled because of the high pricing of Places API Nearby Search
		// const nearbyPlacesGoogle = await geoService.getPlaceForLocation(clonedCurrentJourney.value.destination, true)

		// Use our own poi database and list nearby POIs mapped as places
		const destinationPois = clonedCurrentJourney.value.trips[clonedCurrentJourney.value.trips.length - 1].destination_pois.filter((poi) => typeof poi.name != 'undefined' && poi.name !== '') || []
		const nearbyPlaces = await geoService.getPlacesFromPoi(destinationPois)

		// Add nearby places that are not already in the defaultPlaces array
		nearbyPlaces.forEach((place) => {
			if (!defaultPlaces.value.find((p) => p.place_id === place.place_id && !p.extraTitle)) {
				defaultPlaces.value.push(place)
			}
		})

		// If nearbyPlaces har place with streetAddress equal to locationAddress.streetAddress
		const matchingNearbyPlace = nearbyPlaces.find((place) => place.streetAddress === locationAddress.streetAddress)
		console.warn('ApprovePage: Init', 'matchingNearbyPlace', matchingNearbyPlace, locationAddress, nearbyPlaces)
		if (matchingNearbyPlace) {
			// Set the selected destination to the matching nearby place
			selectedDestination.value = matchingNearbyPlace
			handleDestinationChange(false)
		}

		// Check if destinations in places lookup has distance below 200 meters from current destination
		for (const placeId of Object.keys(dataStore.places)) {
			try {
				const storedPlace = dataStore.places[placeId]
				if (clonedCurrentJourney.value.destination && storedPlace?.latitude && storedPlace?.longitude) {
					const distance = calculateLocationDistance(clonedCurrentJourney.value.destination, storedPlace)
					if (distance < 200) {
						selectedDestination.value = storedPlace
						handleDestinationChange(false)
						break
					}
				}
			} catch (error) {
				console.warn('Error accessing stored place:', placeId, error)
				continue
			}
		}
	} else {
		selectedDestination.value = clonedCurrentJourney.value.destination

		// Try to autocomplete the survey based on the destination
		dataStore.autoCompleteSurveyForJourneyDestination(clonedCurrentJourney.value)
	}

	// Trigger the survey changed event to evaluate current state
	surveyChanged()

	// Fly to the location after a short pause
	setTimeout(() => {
		appStore.bottomSheet.autoResize(500, true, true, 'ApprovePage Init', false, function () {
			console.warn('ApprovePage: autoResize done')

			// Extract coordinates immediately before use
			const dest = clonedCurrentJourney.value.destination
			if (dest && dest.longitude != null && dest.latitude != null) {
				// Create a simple object that can't be mutated
				const location = {
					longitude: dest.longitude,
					latitude: dest.latitude,
				}
				mapStore.flyToLocation(location, 16, 1000)
			} else {
				console.warn('ApprovePage: destination invalid at flyToLocation call', dest)
			}
		})
	}, 200)
}

const clearMapEditTrip = () => {
	mapStore.currentEditTrip = null
	mapStore.setCoordinateChangeListener(null)
}

const goBack = () => {
	clearMapEditTrip()
	appStore.goBack()
}

const flyToDestination = () => {
	if (clonedCurrentJourney.value.destination) {
		mapStore.flyToLocation(clonedCurrentJourney.value.destination, 16, 1000)
	}
}

const safeGetIconForLocation = (item) => {
	try {
		return getIconForLocation(item)
	} catch (error) {
		console.warn('Error in getIconForLocation:', error, item)
		return 'fa-location-dot' // fallback icon
	}
}
</script>

<template>
	<BasePage name="approve" @active="init" title="Formålet med reisen">
		<template v-if="dataStore.currentJourney && !showJourneyDaySurvey">
			<div class="question mb-3">
				<div class="mt-2 mb-2">
					<label class="flex gap-2 nowrap" @click="flyToDestination()">
						Destinasjonen for reisen
						<button class="button tiny transparent" v-if="clonedCurrentJourney?.destination">
							<i class="fa-solid fa-location-crosshairs"></i>
						</button>
					</label>

					<span class="inline-block text-sm font-normal leading-tight opacity-50">
						<template v-if="!selectedDestination">Du må velge eller søke opp det endelige reisemålet for denne reisen. </template>
						<template v-else> Du kan også endre ved å trykke og holde på et punkt i kartet, eller dra i markøren for å flytte den. </template>
					</span>
				</div>

				<select-box class="form destination" :divideindex="surveyPlacesCount" searchable cols2 :options="defaultPlaces" :rememberSearchQuery="true" :min-search-length="2" :search-debounce="400" :remote-search="searchDestination" v-model="selectedDestination" @change="handleDestinationChange" @open="handleDestinationListOpen">
					<template #selected-content="{ selectedValue }">
						<div v-if="selectedValue?.title">
							<div class="flex gap-2">
								<div class="image">
									<i :class="['fas', safeGetIconForLocation(selectedValue)]"></i>
								</div>
								<div v-if="selectedValue.extraTitle">
									{{ selectedValue.extraTitle }}
								</div>
								<div v-else class="name">
									{{ selectedValue.title }}
								</div>
							</div>
							<span>
								{{ selectedValue.subTitle != '' ? selectedValue.subTitle : selectedValue.formattedAddress }}
								{{ selectedValue.type == 'pluscode' && !selectedValue.subTitle && !selectedValue.formattedAddress ? 'Område' : '' }}
							</span>
						</div>
						<div v-else>
							<div class="name">Velg destinasjon</div>
							<span>Søk etter nøyaktig destinasjon for reisen</span>
						</div>
					</template>
					<template #selected-none>
						<span class="placeholder">Velg eller søk destinasjon</span>
					</template>
					<template #item="{ option }">
						<div class="flex">
							<div class="image">
								<i :class="['fas', safeGetIconForLocation(option)]"></i>
							</div>
							<div v-if="option.extraTitle">
								{{ option.extraTitle }}
							</div>
							<div v-else class="name">
								{{ option.title }}
							</div>
						</div>
						<div class="flex space-between">
							<span v-if="option.subTitle">{{ option.subTitle }}</span>
							<span v-if="option.distance" style="color: var(--color-primary)">{{ option.distance }}m</span>
						</div>
					</template>
				</select-box>
			</div>
			<template v-for="(question, questionIndex) in clonedCurrentJourney?.survey" :key="question.id">
				<JourneySurveyQuestion v-if="questionIndex == 0 || (questionIndex > 0 && clonedCurrentJourney?.survey[questionIndex - 1].completed)" :survey="question" @change="surveyChanged" @open="surveyOptionsOpened" />
			</template>
			<div class="mb-4" v-if="appStore.testerMode && clonedCurrentJourney">
				<label for="journeyComment"><strong>Kommentar</strong></label>
				<textarea id="journeyComment" class="form-control mt-1" v-model="clonedCurrentJourney.comment" placeholder="Frivillig kommentar til denne reisen" rows="3"></textarea>
			</div>
		</template>

		<!-- LAST JOURNEY -->
		<template v-if="showJourneyDaySurvey">
			<div v-for="(question, questionIndex) in dataStore.currentJourneyDay?.survey" :key="question.id">
				<!-- Show next question when previous has been answered -->
				<JourneySurveyQuestion v-if="questionIndex == 0 || (questionIndex > 0 && dataStore.currentJourneyDay?.survey[questionIndex - 1].answer)" :survey="question" @open="surveyOptionsOpened" @change="surveyChanged" />
			</div>
		</template>
		<template #footer v-if="showJourneyDaySurvey">
			<div class="button" @click="showJourneyDaySurvey = false">
				<i class="fa-solid fa-rotate-left"></i>
				<span>&nbsp;Tilbake&nbsp;</span>
			</div>
			<div class="button green full" @click="approveJourneyClick()" :class="{ disabled: !validDay }">
				<i class="fas fa-save"></i>
				<span>Fortsett</span>
			</div>
		</template>

		<template #footer v-if="!showJourneyDaySurvey">
			<div class="button" @click="goBack()">
				<i class="fa-solid fa-rotate-left"></i>
				<span>&nbsp;Tilbake&nbsp;</span>
			</div>
			<div class="button green full" @click="approveJourneyClick()" :class="{ disabled: !validJourney }">
				<i class="fas fa-save"></i>
				<span>Godkjenn reise</span>
			</div>
		</template>
	</BasePage>
</template>
